import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TeamContext = createContext(null);

export function TeamProvider({ children }) {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [currentTeamId, setCurrentTeamIdState] = useState(
    () => localStorage.getItem("currentTeamId") || null
  );
  const [loading, setLoading] = useState(true);

  const refreshTeams = useCallback(async () => {
    if (!user) {
      // Auth not resolved yet (or logged out): do NOT flip loading off here,
      // otherwise Dashboard sees teams=[] before auth resolves and flashes to /setup.
      setTeams([]);
      return [];
    }
    try {
      const { data } = await api.get("/teams");
      setTeams(data);
      setCurrentTeamIdState((prev) => {
        if (prev && data.some((t) => t.id === prev)) return prev;
        return data[0]?.id || null;
      });
      return data;
    } catch (e) {
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    refreshTeams();
  }, [refreshTeams]);

  useEffect(() => {
    if (currentTeamId) localStorage.setItem("currentTeamId", currentTeamId);
  }, [currentTeamId]);

  const setCurrentTeamId = (id) => setCurrentTeamIdState(id);

  const currentTeam = teams.find((t) => t.id === currentTeamId) || null;
  const currentRole = currentTeam?.my_role || null;

  return (
    <TeamContext.Provider
      value={{ teams, currentTeam, currentTeamId, currentRole, setCurrentTeamId, refreshTeams, loading }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  return useContext(TeamContext);
}
